import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { UTILS } from "utils";
import { API } from "api";
import { errorHandler } from "api/api/errorHandler";
import {
  useCreateModifierGroupMutation,
  useUnLinkModifierFromGroupMutation,
  useGetModifierGroupMutation,
  useUpdateModifierGroupMutation,
  useGetModifierGroupsMutation,
} from "redux/modifiers/service";

const useGroupModifierApi = ({
  as = "create",
  onSuccess = () => {},
  onClose = () => {},
  modifierGroup = {},
  toBeAttachedModifiersOptions = [],
  toBeUnLinkModifiersOptions = [],
  onAttachModifiers = () => {},
} = {}) => {
  const [createModifierGroup] = useCreateModifierGroupMutation();
  const [updateModifierGroupRequest] = useUpdateModifierGroupMutation();
  const [unLinkModifierFromGroup] = useUnLinkModifierFromGroupMutation();
  const [getModifierGroup] = useGetModifierGroupMutation();
  const [getModifierGroups] = useGetModifierGroupsMutation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const { t } = useTranslation();
  const history = useHistory();

  const linkModifierGroupsWithModifier = async (modifierGroupId) => {
    try {
      setLoading(true);
      const userDetail = UTILS.getCurrentUser();
      const result = await API.LinkModifierWithGroup({
        brandId: userDetail.brandId,
        modifierGroupId: modifierGroupId,
        modifiers: toBeAttachedModifiersOptions?.map(modifier => ({modifierId: modifier._id, price: modifier.price})),
      });
      if (result?.success === 1) {
        UTILS.successNotification(
          result?.message ?? t("Attached Successfully")
        );
        onAttachModifiers(toBeAttachedModifiersOptions);
        onClose();
      } else {
        UTILS.errorNotification(errorHandler(result?.message));
      }
    } catch (error) {
      UTILS.errorNotification(errorHandler(error?.error?.message));
    } finally {
      setLoading(false);
    }
  };

  const addNewModifierGroup = async (isValid, modifierGroupValues) => {
    if (!isValid) return;
    try {
      setLoading(true);
      const userDetail = UTILS.getCurrentUser();
      const data = await createModifierGroup({
        brandId: userDetail.brandId,
        ...modifierGroupValues,
      });
      if (data?.data?.success === 1) {
        if (toBeAttachedModifiersOptions?.length > 0) {
          await linkModifierGroupsWithModifier(data?.data?.data?._id);
        }
        if (as === "modal") {
          onSuccess(data?.data);
          return;
        }
        UTILS.successNotification(
          data?.data?.message ?? t("Modifier Group Created Successfully")
        );
        history.push("/app/dashboard/modifier-group");
      } else {
        UTILS.errorNotification(errorHandler(data?.error?.data));
      }
    } catch (error) {
      UTILS.errorNotification(errorHandler(error?.message));
    } finally {
      setLoading(false);
    }
  };

  const unlinkModFromGroup = async ({modifierId, groupId, OnSuccess = () => {}, handleCancel = () => {}}) => {
    setLoading(true);
    try {
      const userDetail = UTILS.getCurrentUser();
      const data = await unLinkModifierFromGroup({
        brandId: userDetail.brandId,
        modifierId: modifierId,
        modifierGroupId: groupId,
      });
      if (data?.data?.success === 1) {
        UTILS.successNotification(
          data?.data?.message ?? t("Unlinked Successfully")
        );
        OnSuccess();
      } else {
        UTILS.errorNotification(errorHandler(data?.error?.data));
      }
    } catch (error) {
      UTILS.errorNotification(errorHandler(error?.message));
    } finally {
      handleCancel();
      setLoading(false);
    }
  };


  const sortModiferGroup = async ({group, selected, modifierGroupId, OnSuccess = () => {}}) => {
    try {
      const userDetail = UTILS.getCurrentUser();
      setLoading(true);
      const result = await API.sortModifierOption({
        brandId: userDetail.brandId,
        modifierGroupId: modifierGroupId,
        modifierId: selected?._id,
        index: group.newIndex,
      });
      if (result?.success === 1) {
        UTILS.successNotification(result?.message ?? t("Sorted Successfully"));
      } else {
        UTILS.errorNotification(errorHandler(result?.message) );
      }
    } catch (error) {
      UTILS.errorNotification(errorHandler(error?.error?.message));
    } finally {
      OnSuccess();
      setLoading(false);
    }
  };

  const updateModifierGroup = async ({ isValid, modifierGroupValues }) => {
    if (!isValid) return;
    setLoading(true);
    try {
      const userDetail = UTILS.getCurrentUser();
      const data = await updateModifierGroupRequest({
        brandId: userDetail.brandId,
        modifierGroupId: history?.location?.state?.modifier?._id,
        ...modifierGroupValues,
      });
      if (data?.data?.success === 1) {
        if (toBeAttachedModifiersOptions?.length > 0) {

          await linkModifierGroupsWithModifier(history?.location?.state?.modifier?._id);
        }
        if (toBeUnLinkModifiersOptions?.length > 0) {
          for (const modifier of toBeUnLinkModifiersOptions) {
            await unlinkModFromGroup({modifierId: modifier._id, groupId: history?.location?.state?.modifier?._id});
          }
        }
        if (as === "modal") {
          onSuccess(data);
          return;
        }
        UTILS.successNotification(
          data?.data?.message ?? t("Modifier Group Updated Successfully")
        );
        history.push("/app/dashboard/modifier-group");
      } else {
        UTILS.errorNotification(errorHandler(data?.error?.data));
      }
    } catch (error) {
      UTILS.errorNotification(errorHandler(error?.message));
    } finally {
      setLoading(false);
    }
  };

  const getAttachedModifiers = async (setModifiers = () => {},setM = () => {},modifierGroupId = null) => {
    if (!history?.location?.state?.modifier?._id && !modifierGroupId) return;
    setLoading(true);
    try {
      const userDetail = UTILS.getCurrentUser();
      const { data } = await getModifierGroup({
        brandId: userDetail.brandId,
        modifierGroupId: history?.location?.state?.modifier?._id || modifierGroupId,
      });
      if (data?.success === 1) {
        setModifiers(data?.data?.modifiers);
        setM((prevState) => ({
          ...prevState,
          values: {
            ...prevState.values,
            ...data.data,
          },
        }));
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };


   const getAllModiferGroups = async () => {
    setLoading(true);
    try {
      const userDetail = UTILS.getCurrentUser();
      const { data } = await getModifierGroups({
        brandId: userDetail.brandId,
      });
      if (data?.success === 1) {
        setData(data.data);
      }
      return data;
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const sortModiferGroupWithItem = async ({group, selected, itemId, OnSuccess = () => {}}) => {
    try {
      const userDetail = UTILS.getCurrentUser();
      setLoading(true);
      const result = await API.sortModifierGroup({
        brandId: userDetail.brandId,
        itemId: itemId,
        modifierGroupId: selected?._id,
        index: group.newIndex,
      });
      if (result?.success === 1) {
        UTILS.successNotification(result?.message ?? t("sorted Successfully"));
      } else {
        UTILS.errorNotification(errorHandler(result?.message));
      }
    } catch (error) {
      UTILS.errorNotification(errorHandler(error?.error?.message));
    } finally {
      setLoading(false);
    }
  };

  const linkModifierGroupsWithItems = async ({itemId, attachedGroups, OnSuccess = () => {}}) => {
    try {
      setLoading(true);
      const userDetail = UTILS.getCurrentUser();
      const result = await API.linkModifierGroupWithItem({
        brandId: userDetail.brandId,
        itemId: itemId,
        modifierGroups: attachedGroups?.map(group => ({modifierGroupId: group?._id})),
      });
      if (result?.success === 1) {
        UTILS.successNotification(
          result?.message ?? t("Attached Successfully")
        );
        OnSuccess(result);
      } else {
        UTILS.errorNotification(errorHandler(result?.message));
      }
    } catch (error) {
      UTILS.errorNotification(errorHandler(error?.error?.message));
    } finally {
      setLoading(false);
    }
  };

  const UnlinkModiferGroupFromItem = async ({itemId, modifierGroupId, OnSuccess = () => {}}) => {
    try {
      setLoading(true);
      const userDetail = UTILS.getCurrentUser();
      const response = await API.unLinkModifierGroupWithItem({
        brandId: userDetail.brandId,
        itemId: itemId,
        modifierGroupId: modifierGroupId,
      });
      if (response?.success === 1) {
        UTILS.successNotification(
          response?.message ?? t("Unlinked Successfully")
        );
        OnSuccess(response);
      } else {
        UTILS.errorNotification(errorHandler(response?.message));
      }
    } catch (error) {
      UTILS.errorNotification(errorHandler(error?.error?.message));
    } finally { 
      setLoading(false);
    }
  };
  

  return {
    loading,
    addNewModifierGroup,
    linkModifierGroupsWithItems,
    unlinkModFromGroup,
    sortModiferGroup,
    updateModifierGroup,
    getAttachedModifiers,
    getAllModiferGroups,
    sortModiferGroupWithItem,
    UnlinkModiferGroupFromItem,
    data,
  };
};

export default useGroupModifierApi;
